import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLeaveRequestInput,
  ItemResponse,
  LeaveRequestResponse,
  LeaveStatus,
  LeaveType,
  ListResponse,
  ReviewLeaveRequestInput,
  UpdateLeaveRequestInput,
  VacationAnalysisResponse,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const LEAVE_KEY = ['leave'] as const;

export interface LeaveFilters {
  status?: LeaveStatus;
  type?: LeaveType;
  year?: number;
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
}

function buildQuery(filters: LeaveFilters): string {
  const params = new URLSearchParams({ pageSize: '500' });
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.year) params.set('year', String(filters.year));
  if (filters.employeeId) params.set('employeeId', filters.employeeId);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  return params.toString();
}

function useInvalidateLeave() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: LEAVE_KEY });
}

export function useMyLeaveRequests(filters: LeaveFilters = {}) {
  return useQuery({
    queryKey: [...LEAVE_KEY, 'mine', filters],
    queryFn: () => apiRequest<ListResponse<LeaveRequestResponse>>(`/v1/leave-requests?${buildQuery(filters)}`),
  });
}

export function useAllLeaveRequests(filters: LeaveFilters = {}, enabled = true) {
  return useQuery({
    queryKey: [...LEAVE_KEY, 'all', filters],
    queryFn: () => apiRequest<ListResponse<LeaveRequestResponse>>(`/v1/leave-requests/all?${buildQuery(filters)}`),
    enabled,
  });
}

export function useLeaveAnalysis(year: number, enabled = true) {
  return useQuery({
    queryKey: [...LEAVE_KEY, 'analysis', year],
    queryFn: () => apiRequest<ItemResponse<VacationAnalysisResponse>>(`/v1/leave-requests/analysis?year=${year}`),
    enabled,
  });
}

export function useLeaveCalendar(year: number, month: number) {
  return useQuery({
    queryKey: [...LEAVE_KEY, 'calendar', year, month],
    queryFn: () =>
      apiRequest<ListResponse<LeaveRequestResponse>>(`/v1/leave-requests/calendar?year=${year}&month=${month}`),
  });
}

export function useCreateLeaveRequest() {
  const invalidate = useInvalidateLeave();
  return useMutation({
    mutationFn: (input: CreateLeaveRequestInput) =>
      apiRequest<ItemResponse<LeaveRequestResponse>>('/v1/leave-requests', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useUpdateLeaveRequest() {
  const invalidate = useInvalidateLeave();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLeaveRequestInput }) =>
      apiRequest<ItemResponse<LeaveRequestResponse>>(`/v1/leave-requests/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useDeleteLeaveRequest() {
  const invalidate = useInvalidateLeave();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ data: { id: string } }>(`/v1/leave-requests/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useApproveLeave() {
  const invalidate = useInvalidateLeave();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReviewLeaveRequestInput }) =>
      apiRequest<ItemResponse<LeaveRequestResponse>>(`/v1/leave-requests/${id}/approve`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useRejectLeave() {
  const invalidate = useInvalidateLeave();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReviewLeaveRequestInput }) =>
      apiRequest<ItemResponse<LeaveRequestResponse>>(`/v1/leave-requests/${id}/reject`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}
